import './settings.css';
import { IonButton, IonButtons, IonContent, IonHeader, IonTitle, IonToolbar } from '@ionic/react';
import type { FederalState } from '../../domain/holidays';
import { useMessages } from '../i18n/LanguageProvider';
import type { Language } from '../i18n/messages';
import { useAppData } from '../state/AppDataProvider';
import { AppearanceSection } from './AppearanceSection';
import { BackupSection } from './BackupSection';
import { ClosedDaysSection } from './ClosedDaysSection';
import { stateOptions } from './settings-view';
import { useCalendarChange } from './use-calendar-change';

// DE: Jede Sprache heißt so, wie ihre Sprecher sie nennen.
// EN: Each language is named the way its speakers name it.
const LANGUAGE_NAMES: Readonly<Record<Language, string>> = { uk: 'Українська', de: 'Deutsch' };

function LanguageSection() {
  const { dispatch } = useAppData();
  const { language, t } = useMessages();
  return (
    <section className="app-card">
      <h2>{t.languageTitle}</h2>
      <select
        className="app-input"
        aria-label={t.languageTitle}
        value={language}
        onChange={(event) =>
          dispatch({ type: 'set-language', language: event.target.value as Language })
        }
      >
        {(['uk', 'de'] as const).map((id) => (
          <option key={id} value={id}>
            {LANGUAGE_NAMES[id]}
          </option>
        ))}
      </select>
    </section>
  );
}

function PlaceSection() {
  const { data } = useAppData();
  const { language, t } = useMessages();
  const changeCalendar = useCalendarChange();
  const choose = (value: string) =>
    changeCalendar({
      type: 'set-federal-state',
      federalState: value === '' ? undefined : (value as FederalState),
    });
  return (
    <section className="app-card">
      <h2>{t.federalStateTitle}</h2>
      <p className="app-muted">{t.federalStateHint}</p>
      <select
        className="app-input"
        aria-label={t.federalStateTitle}
        value={data.household.federalState ?? ''}
        onChange={(event) => choose(event.target.value)}
      >
        <option value="">{t.notChosen}</option>
        {stateOptions(language).map((option) => (
          <option key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </select>
    </section>
  );
}

/**
 * DE: Einstellungen: Sprache, Darstellung, Bundesland, eigene Ruhetage und Sicherung.
 * EN: Settings: language, appearance, federal state, own closed days and backup.
 */
export function SettingsPage({ onClose }: { onClose: () => void }) {
  const { t } = useMessages();
  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{t.settings}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={onClose}>{t.close}</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="app-page">
          <LanguageSection />
          <AppearanceSection />
          <PlaceSection />
          <ClosedDaysSection />
          <BackupSection />
        </div>
      </IonContent>
    </>
  );
}
